// Csapatchat — közös formázók (idő, avatar, szöveg).

const PALETTE = ['#C77B9B', '#5E7FA8', '#6E9663', '#B9834A', '#9A6E9E', '#C9A45C'];

/** Ugyanaz a szín-szabály, mint a bal menü profilképénél (név alapján). */
export function avatarColor(name: string): string {
  let hash = 0;
  for (const ch of name || '') hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[hash % PALETTE.length];
}

export function initials(name: string): string {
  const parts = `${name || ''}`.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : parts[0].slice(0, 2)).toUpperCase();
}

export function firstName(name: string): string {
  // magyar névsorrend: „Kovács Emese" → Emese
  const parts = `${name || ''}`.trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? parts[parts.length - 1] : parts[0] || '';
}

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

const time = (d: Date) => d.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });

/** A beszélgetés-listában: ma → 14:32, tegnap, a héten → hétfő, régebben → szept. 12. */
export function listTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (days <= 0) return time(d);
  if (days === 1) return 'tegnap';
  if (days < 7) return d.toLocaleDateString('hu-HU', { weekday: 'short' });
  return d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
}

/** Napelválasztó a beszélgetésben. */
export function dayLabel(iso: string): string {
  const d = new Date(iso);
  const days = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86400000);
  if (days <= 0) return 'Ma';
  if (days === 1) return 'Tegnap';
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const date = d.toLocaleDateString('hu-HU', { year: sameYear ? undefined : 'numeric', month: 'long', day: 'numeric' });
  return days < 7 ? `${date}, ${d.toLocaleDateString('hu-HU', { weekday: 'long' })}` : date;
}

export function clock(iso: string | null | undefined): string {
  return iso ? time(new Date(iso)) : '';
}

/** Teljes időpont (buborék-tooltip, „Látta" lista): 2026. szeptember 23. 14:32 */
export function fullTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long', day: 'numeric' })} ${time(d)}`;
}

/** „Aktív 5 perce" / „Aktív 2 órája" — a privát beszélgetés fejlécében. */
export function lastSeenLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const minutes = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (minutes < 60) return `Aktív ${minutes} perce`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `Aktív ${hours} órája`;
  return `Aktív ${listTime(iso)}`;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1).replace('.', ',')} MB`;
}

export function fileExt(name: string): string {
  const ext = name.includes('.') ? name.split('.').pop() || '' : '';
  return ext.slice(0, 4).toUpperCase() || 'FÁJL';
}

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] as string);

/**
 * Üzenetszöveg HTML-ként: escapelve, a webcímek kattinthatók, a T-12 / L-5 azonosítók az ERP-n
 * belül a taskra / levelezésre visznek (.chat-idlink — a komponens a routerrel nyitja meg; data-*
 * attribútumot az Angular sanitizere kiszűrne, ezért a cél a href-ben van).
 */
export function linkify(text: string): string {
  const escaped = escapeHtml(text || '');
  return escaped
    .replace(/\bhttps?:\/\/[^\s<]+[^\s<.,:;!?)\]'"]/gi, (url) => `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`)
    .replace(/(^|[\s(])(T|L)-(\d{1,7})\b/g, (_m, pre, kind, id) => {
      const route = kind === 'T' ? `/support?ticket=${id}` : `/support/levelezes?thread=${id}`;
      return `${pre}<a class="chat-idlink" href="${route}">${kind}-${id}</a>`;
    });
}

/** Csak emoji (legfeljebb 3) — ilyenkor nagyobb, buborék nélküli megjelenés. */
export function isEmojiOnly(text: string): boolean {
  const t = `${text || ''}`.trim();
  if (!t || t.length > 16) return false;
  return /^(?:\p{Extended_Pictographic}(?:️|‍\p{Extended_Pictographic}|\p{Emoji_Modifier})*\s*){1,3}$/u.test(t);
}
